/**
 * ReflectionDetailPage Component
 *
 * Page for viewing a reflection with synchronized audio player and transcript.
 * Features a sticky header with tabs, grid layout with sidebar on transcript view,
 * and a sticky audio player at the bottom.
 * Does not use the main Layout component to maximize screen space for content.
 * Design matches Magic Patterns design specification.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Loader2,
  Share2,
  Link2,
  MoreHorizontal,
  FileText,
  BarChart2,
  MessageSquare,
  AlertCircle,
  Search,
  Users,
  Settings2,
  Lightbulb,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AudioPlayer, AudioPlayerRef } from "@/components/audio";
import { TranscriptView, TranscriptStatsBar } from "@/components/transcript";
import type { TranscriptViewHandle } from "@/components/transcript";
import { ProcessingReflectionView } from "@/components/reflections/ProcessingReflectionView";
import { SpeakerConfirmationRequired } from "@/components/reflections/SpeakerConfirmationRequired";
import { ConversationSettingsPanel } from "@/components/reflections/ConversationSettingsPanel";
import { InsightsSidebar } from "@/components/reflections/InsightsSidebar";
import { StatsOverview } from "@/components/reflections/StatsOverview";
import { ShareModal } from "@/components/reflections/ShareModal";
import { TherapistChat } from "@/components/chat/TherapistChat";
import {
  SpeakerBindingBanner,
  SpeakerBindingWizard,
} from "@/components/speaker-binding";
import { MobileSpeakerSheet } from "@/components/reflections/MobileSpeakerSheet";
import { useReflection, useTranscript } from "@/hooks";
import { useAnnotations } from "@/hooks/useAnnotations";
import { useShares } from "@/hooks/useShares";
import { useFeedback, useRegenerateFeedback } from "@/hooks/useFeedback";
import { useAnalysisStatus } from "@/hooks/useAnalysisStatus";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSpeakerMemory } from "@/hooks/useSpeakerMemories";
import { useConversationPartners } from "@/hooks/useConversationPartners";
import { Brain } from "lucide-react";
import { getAudioUrl, getDownloadUrl } from "@/services/recordings";
import {
  updateSpeaker,
  getConversationSentiment,
} from "@/services/transcripts";
import { deleteChat, generateChatSummary } from "@/services/chat";
import { generateAnnotations } from "@/services/annotations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Format duration in milliseconds to human-readable string
 */
function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);

  if (minutes === 0) {
    return `${totalSeconds} sec`;
  }
  return `${minutes} min`;
}

type Tab = "transcript" | "summary" | "chat";

export function ReflectionDetailPage() {
  const { reflectionId } = useParams<{ reflectionId: string }>();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") as Tab | null;

  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(initialTab || "transcript");
  const [searchQuery, setSearchQuery] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSpeakerWizard, setShowSpeakerWizard] = useState(false);
  const [showMobileSpeakerSheet, setShowMobileSpeakerSheet] = useState(false);
  const [showMobileInsights, setShowMobileInsights] = useState(false);
  const [dismissedBindingBanner, setDismissedBindingBanner] = useState(false);
  const [isRegeneratingFeedback, setIsRegeneratingFeedback] = useState(false);
  const [feedbackTimestampBeforeRegen, setFeedbackTimestampBeforeRegen] =
    useState<string | null>(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showKeyMoments, setShowKeyMoments] = useState(() => {
    const stored = localStorage.getItem("attunewell:show-key-moments");
    return stored !== null ? stored === "true" : true;
  });
  const audioPlayerRef = useRef<AudioPlayerRef>(null);
  const transcriptViewRef = useRef<TranscriptViewHandle>(null);

  const {
    reflection,
    isLoading: reflectionLoading,
    error: reflectionError,
    refetch: refetchReflection,
  } = useReflection(reflectionId);

  const recordingId = reflection?.recording?.id;
  const recordingStatus = reflection?.recording?.status;

  // Share links
  const { data: shares } = useShares(reflectionId);
  const activeShareCount = (shares || []).filter(
    (s) => s.is_active && new Date(s.expires_at) > new Date()
  ).length;

  // Fetch transcript when recording is completed OR awaiting speaker confirmation
  // (transcript exists in both states)
  const {
    transcript,
    segments,
    speakers,
    isLoading: transcriptLoading,
    isFetching: transcriptFetching,
    refetch: refetchTranscript,
  } = useTranscript(recordingId, {
    enabled:
      recordingStatus === "completed" ||
      recordingStatus === "awaiting_speaker_confirmation",
  });

  const transcriptId = transcript?.id;

  // Fetch annotations for insights
  const { data: annotationsData, isLoading: annotationsLoading } =
    useAnnotations(transcriptId, { enabled: !!transcriptId });

  // Fetch feedback for stats overview
  const isCompleted = reflection?.recording?.status === "completed";
  const {
    data: feedback,
    isLoading: feedbackLoading,
    refetch: refetchFeedback,
  } = useFeedback(reflectionId || "", isCompleted);

  // Fetch conversation-level sentiment for emotional overview card
  const { data: conversationSentiment } = useQuery({
    queryKey: ["conversationSentiment", recordingId],
    queryFn: () => getConversationSentiment(recordingId!),
    enabled: !!recordingId && isCompleted,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes("404")) return false;
      return failureCount < 2;
    },
  });

  const handleToggleKeyMoments = useCallback(() => {
    setShowKeyMoments((prev) => {
      const next = !prev;
      localStorage.setItem("attunewell:show-key-moments", String(next));
      return next;
    });
  }, []);

  const keyMomentMap = useMemo(() => {
    if (!conversationSentiment || !showKeyMoments) return undefined;

    const map = new Map<number, "tension" | "positive">();

    // Add tension peaks first
    const peaks = conversationSentiment.tension_peaks as Array<{
      segment_sequence: number;
      time_ms: number;
      description: string;
    }>;
    if (Array.isArray(peaks)) {
      for (const peak of peaks) {
        if (typeof peak.segment_sequence === "number") {
          map.set(peak.segment_sequence, "tension");
        }
      }
    }

    // Positive moments override tension (represents resolution)
    const moments = conversationSentiment.positive_moments as Array<{
      segment_sequence: number;
      time_ms: number;
      description: string;
    }>;
    if (Array.isArray(moments)) {
      for (const moment of moments) {
        if (typeof moment.segment_sequence === "number") {
          map.set(moment.segment_sequence, "positive");
        }
      }
    }

    return map.size > 0 ? map : undefined;
  }, [conversationSentiment, showKeyMoments]);

  const hasKeyMoments = useMemo(() => {
    if (!conversationSentiment) return false;
    const peaks = conversationSentiment.tension_peaks;
    const moments = conversationSentiment.positive_moments;
    return (
      (Array.isArray(peaks) && peaks.length > 0) ||
      (Array.isArray(moments) && moments.length > 0)
    );
  }, [conversationSentiment]);

  // Mutation for regenerating feedback with a new therapy style
  const queryClient = useQueryClient();
  const { mutate: triggerRegenerateFeedback } = useRegenerateFeedback(
    reflectionId || ""
  );

  // Fetch speaker memory if reflection has a conversation partner
  const conversationPartnerId =
    reflection?.conversation_partner_id ?? undefined;
  const { data: speakerMemory } = useSpeakerMemory(conversationPartnerId);
  const { data: partnersData } = useConversationPartners();
  const memoryPartner = partnersData?.partners.find(
    (p) => p.id === conversationPartnerId
  );

  // Get auth for SSE connection
  const { getAccessToken } = useAuth();

  // Determine if speakers are confirmed for analysis (needed before using hook)
  const speakersConfirmedForAnalysis =
    transcript?.speakers_confirmed_for_analysis ?? false;

  // Track analysis generation status (feedback/insights stages)
  const {
    summaryStatus,
    analysisStatus,
    insightsStatus,
    retry: retryAnalysis,
  } = useAnalysisStatus({
    reflectionId,
    isCompleted: isCompleted || false,
    speakersConfirmed: speakersConfirmedForAnalysis,
    getAccessToken,
    onComplete: () => {
      // Refetch data when analysis completes
      refetchFeedback();
      if (transcriptId) {
        queryClient.invalidateQueries({
          queryKey: ["annotations", transcriptId],
        });
      }
    },
  });

  // Poll for feedback updates while regenerating
  useEffect(() => {
    if (!isRegeneratingFeedback) return;

    // Poll every 3 seconds while regenerating
    const interval = setInterval(() => {
      refetchFeedback();
      // Also refetch annotations
      if (transcriptId) {
        queryClient.invalidateQueries({
          queryKey: ["annotations", transcriptId],
        });
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [isRegeneratingFeedback, refetchFeedback, queryClient, transcriptId]);

  // Poll for feedback/annotation data while analysis is generating
  // This is a fallback for when SSE events don't propagate across processes
  // (e.g., Celery worker publishes to its own in-memory event bus)
  const isAnalysisGenerating =
    speakersConfirmedForAnalysis &&
    isCompleted &&
    (summaryStatus.isGenerating ||
      analysisStatus.isGenerating ||
      // Also poll if speakers are confirmed but we have no data yet
      (!feedback && !annotationsData?.summary?.summary_text));

  useEffect(() => {
    if (!isAnalysisGenerating || isRegeneratingFeedback) return;

    const interval = setInterval(() => {
      refetchFeedback();
      if (transcriptId) {
        queryClient.invalidateQueries({
          queryKey: ["annotations", transcriptId],
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [
    isAnalysisGenerating,
    isRegeneratingFeedback,
    refetchFeedback,
    queryClient,
    transcriptId,
  ]);

  // Scroll to a specific block when navigated with ?block=N
  useEffect(() => {
    const blockParam = searchParams.get("block");
    if (!blockParam || !segments || segments.length === 0) return;

    // Ensure we're on the transcript tab
    setActiveTab("transcript");

    // Wait a tick for the DOM to render, then scroll
    const timer = setTimeout(() => {
      const blockEl = document.querySelector(
        `[data-block="${blockParam}"]`
      ) as HTMLElement | null;
      if (blockEl) {
        blockEl.scrollIntoView({ behavior: "smooth", block: "center" });
        // Briefly highlight the block
        blockEl.classList.add("ring-2", "ring-primary", "ring-offset-2");
        setTimeout(() => {
          blockEl.classList.remove("ring-2", "ring-primary", "ring-offset-2");
        }, 3000);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchParams, segments, setActiveTab]);

  // Fetch presigned audio URL when recording is available
  useEffect(() => {
    if (recordingId && !audioUrl && !audioLoading) {
      setAudioLoading(true);
      getAudioUrl(recordingId)
        .then((response) => {
          setAudioUrl(response.url);
        })
        .catch((err) => {
          console.error("Failed to get audio URL:", err);
        })
        .finally(() => {
          setAudioLoading(false);
        });
    }
  }, [recordingId, audioUrl, audioLoading]);

  // Handle audio download via presigned URL
  const handleDownload = useCallback(async () => {
    if (!recordingId) return;
    try {
      const { url, filename } = await getDownloadUrl(recordingId);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download audio:", err);
    }
  }, [recordingId]);

  // Handle time update from audio player
  const handleTimeUpdate = useCallback((timeMs: number) => {
    setCurrentTimeMs(timeMs);
  }, []);

  // Handle seek from transcript click
  const handleSeek = useCallback((timeMs: number) => {
    audioPlayerRef.current?.seekTo(timeMs);
    setCurrentTimeMs(timeMs);
  }, []);

  // Handle annotation click from InsightsSidebar - scroll to the segment
  const handleAnnotationClick = useCallback(
    (annotation: {
      segment_id: string | null;
      references_segment_ids?: string[] | null;
    }) => {
      const targetId =
        annotation.segment_id ?? annotation.references_segment_ids?.[0];
      if (!targetId) return;
      // Find the segment with this ID to get its start time
      const segment = segments.find((s) => s.id === targetId);
      if (segment) {
        handleSeek(segment.start_time_ms);
        setActiveTab("transcript");
        // Scroll via ref (with fallback delay for tab switch)
        setTimeout(() => {
          transcriptViewRef.current?.scrollToSegment(targetId);
        }, 100);
      }
    },
    [segments, handleSeek]
  );

  // Handle processing complete - refetch both reflection (for status) and transcript
  const handleProcessingComplete = useCallback(() => {
    refetchReflection();
    refetchTranscript();
  }, [refetchReflection, refetchTranscript]);

  // Handle speaker name update
  const handleSpeakerNameUpdate = useCallback(
    async (speakerId: string, newName: string) => {
      try {
        await updateSpeaker(speakerId, newName);
        // Refetch transcript to get updated speaker names
        refetchTranscript();
        // Invalidate annotations (speaker tokens are replaced at fetch time)
        if (transcriptId) {
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey[0] === "annotations" &&
              query.queryKey[1] === transcriptId,
          });
        }
        // Invalidate feedback and chat (speaker tokens are replaced at fetch time)
        if (reflectionId) {
          queryClient.invalidateQueries({
            queryKey: ["feedback", reflectionId],
          });
          queryClient.invalidateQueries({
            queryKey: ["chat", reflectionId],
          });
        }
      } catch (error) {
        console.error("Failed to update speaker name:", error);
      }
    },
    [refetchTranscript, transcriptId, reflectionId, queryClient]
  );

  // Handle therapy style change - regenerate all content with new style
  const handleTherapyStyleChange = useCallback(
    async (styleId: string) => {
      // Store current feedback timestamp before regeneration
      setFeedbackTimestampBeforeRegen(feedback?.generated_at || null);
      setIsRegeneratingFeedback(true);

      // Delete existing chat so it will be recreated with the new therapy style
      if (reflectionId) {
        try {
          await deleteChat(reflectionId);
          // Invalidate the chat query so it will be recreated fresh
          queryClient.invalidateQueries({ queryKey: ["chat", reflectionId] });
        } catch {
          // Chat might not exist, which is fine
        }
      }

      // Regenerate annotations with new therapy style
      if (transcriptId) {
        try {
          await generateAnnotations(transcriptId, {
            therapy_style_id: styleId,
            regenerate: true,
          });
        } catch (error) {
          console.error("Failed to regenerate annotations:", error);
        }
      }

      // Regenerate feedback with new therapy style
      triggerRegenerateFeedback(styleId, {
        onSuccess: () => {
          // Invalidate annotations to refetch the regenerated ones
          if (transcriptId) {
            queryClient.invalidateQueries({
              queryKey: ["annotations", transcriptId],
            });
          }
          // Refetch reflection to get updated therapy_style_id
          refetchReflection();
        },
        onError: () => {
          // Reset regenerating state on error
          setIsRegeneratingFeedback(false);
          setFeedbackTimestampBeforeRegen(null);
        },
      });
    },
    [
      triggerRegenerateFeedback,
      transcriptId,
      reflectionId,
      queryClient,
      refetchReflection,
      feedback?.generated_at,
    ]
  );

  // Detect when feedback regeneration completes by checking if timestamp changed
  useEffect(() => {
    if (
      isRegeneratingFeedback &&
      feedbackTimestampBeforeRegen &&
      feedback?.generated_at &&
      feedback.generated_at !== feedbackTimestampBeforeRegen
    ) {
      // Feedback has been regenerated (new timestamp)
      setIsRegeneratingFeedback(false);
      setFeedbackTimestampBeforeRegen(null);
    }
  }, [
    isRegeneratingFeedback,
    feedbackTimestampBeforeRegen,
    feedback?.generated_at,
  ]);

  // Handle spacebar to toggle play/pause (when not focused on interactive elements)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle spacebar
      if (e.code !== "Space") return;

      // Use activeElement for reliable detection of focused element
      const activeElement = document.activeElement as HTMLElement | null;
      if (!activeElement) return;

      // Don't trigger if focused on any interactive element that uses spacebar
      const tagName = activeElement.tagName.toUpperCase();
      const isInteractiveElement =
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        tagName === "BUTTON" ||
        activeElement.isContentEditable ||
        activeElement.getAttribute("role") === "textbox" ||
        activeElement.getAttribute("role") === "button" ||
        activeElement.getAttribute("role") === "searchbox" ||
        activeElement.getAttribute("contenteditable") === "true";

      if (isInteractiveElement) return;

      // Prevent default scrolling behavior
      e.preventDefault();

      // Toggle play/pause and enable auto-scroll when starting
      if (audioPlayerRef.current) {
        audioPlayerRef.current.togglePlay();
        setAutoScroll(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Determine display states
  const isJournalReflection = reflection?.content_type === "journal";
  const isProcessing = reflection?.recording?.status === "processing";
  const isPending = reflection?.recording?.status === "pending";
  const hasFailed = reflection?.recording?.status === "failed";
  // For journals, no recording is expected
  const hasNoRecording = !reflection?.recording && !isJournalReflection;

  // Handle generating summary for journal reflections
  const handleGenerateSummary = useCallback(async () => {
    if (!reflectionId) return;
    setIsGeneratingSummary(true);
    try {
      await generateChatSummary(reflectionId);
      // Start polling for feedback updates
      const interval = setInterval(async () => {
        const result = await refetchFeedback();
        if (result.data) {
          clearInterval(interval);
          setIsGeneratingSummary(false);
        }
      }, 2000);
      // Timeout after 60 seconds
      setTimeout(() => {
        clearInterval(interval);
        setIsGeneratingSummary(false);
      }, 60000);
    } catch (error) {
      console.error("Failed to generate summary:", error);
      setIsGeneratingSummary(false);
    }
  }, [reflectionId, refetchFeedback]);

  // Set default tab to chat for journal reflections (unless URL specifies otherwise)
  useEffect(() => {
    if (isJournalReflection && !initialTab) {
      setActiveTab("chat");
    }
  }, [isJournalReflection, initialTab]);

  // Format date for display
  const formattedDate = reflection
    ? new Date(reflection.created_at).toLocaleDateString()
    : "";
  const duration = reflection?.duration || 0;

  // Loading state
  if (reflectionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">
          Loading reflection...
        </span>
      </div>
    );
  }

  // Error state
  if (reflectionError || !reflection) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
          <div className="container mx-auto px-4 h-16 flex items-center">
            <Link to="/reflections" aria-label="Back to reflections">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 dark:bg-red-600/10 mb-6">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Reflection Not Found
            </h2>
            <p className="text-muted-foreground mb-6">
              {reflectionError?.message ||
                "The reflection you're looking for doesn't exist."}
            </p>
            <Link to="/reflections">
              <Button>Back to Reflections</Button>
            </Link>
          </Card>
        </main>
      </div>
    );
  }

  // Processing state (only pending and processing - NOT awaiting speaker confirmation)
  // When awaiting speaker confirmation, we show the main UI with transcript
  if (isProcessing || isPending) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
          <div className="container mx-auto px-4 h-16 flex items-center gap-4">
            <Link to="/reflections" aria-label="Back to reflections">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-foreground line-clamp-1">
                {reflection.title || "Untitled Reflection"}
              </h1>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{formattedDate}</span>
                {duration > 0 && (
                  <>
                    <span>•</span>
                    <span>{formatDuration(duration)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          {reflectionId && (
            <ProcessingReflectionView
              reflectionId={reflectionId}
              onComplete={handleProcessingComplete}
            />
          )}
        </main>
      </div>
    );
  }

  // Use the speakers confirmed status (already defined above for useAnalysisStatus)
  const speakersConfirmed = speakersConfirmedForAnalysis;

  // Failed state
  if (hasFailed) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
          <div className="container mx-auto px-4 h-16 flex items-center gap-4">
            <Link to="/reflections" aria-label="Back to reflections">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-foreground line-clamp-1">
                {reflection.title || "Untitled Reflection"}
              </h1>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 dark:bg-red-600/10 mb-6">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Processing Failed
            </h2>
            <p className="text-muted-foreground mb-6">
              We encountered an error while processing your recording. Please
              try uploading it again.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/reflections">
                <Button>Back to Reflections</Button>
              </Link>
            </div>
          </Card>
        </main>
      </div>
    );
  }

  // No recording attached state
  if (hasNoRecording) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur">
          <div className="container mx-auto px-4 h-16 flex items-center gap-4">
            <Link to="/reflections" aria-label="Back to reflections">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-foreground line-clamp-1">
                {reflection.title || "Untitled Reflection"}
              </h1>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
              <AlertCircle className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">
              No Audio Recording
            </h2>
            <p className="text-muted-foreground mb-6">
              There is no audio recording attached to this reflection. Please
              upload an audio file to get started.
            </p>
            <Link to="/reflections">
              <Button>Back to Reflections</Button>
            </Link>
          </Card>
        </main>
      </div>
    );
  }

  // Normal reflection view (completed)
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Sticky Header with Tabs - Frosted glass effect */}
      <header className="sticky top-0 z-40 border-b border-border/40 bg-background/50 backdrop-blur-md supports-[backdrop-filter]:bg-background/40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <Link to="/reflections" aria-label="Back to reflections">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 -ml-2 text-muted-foreground hover:text-foreground flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold tracking-tight text-foreground line-clamp-1">
                {reflection.title || "Untitled Reflection"}
              </h1>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{formattedDate}</span>
                {duration > 0 && (
                  <>
                    <span>•</span>
                    <span>{formatDuration(duration)}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {activeShareCount > 0 && (
              <button
                onClick={() => setShowShareModal(true)}
                className="hidden sm:inline-flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded-full hover:bg-primary/20 transition-colors"
              >
                <Link2 className="w-3 h-3" />
                Shared
              </button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="hidden sm:flex gap-2"
              onClick={() => setShowShareModal(true)}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="sm:hidden"
                  onClick={() => setShowShareModal(true)}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
                {transcriptId && audioUrl && (
                  <DropdownMenuItem onClick={() => setShowSpeakerWizard(true)}>
                    <Users className="w-4 h-4 mr-2" />
                    Review Speakers
                  </DropdownMenuItem>
                )}
                {recordingId && audioUrl && (
                  <DropdownMenuItem onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" />
                    Download audio
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem>Edit details</DropdownMenuItem>
                <DropdownMenuItem>Download transcript</DropdownMenuItem>
                <DropdownMenuItem className="text-destructive">
                  Delete reflection
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Tabs in Header with Search/Auto-scroll */}
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between gap-4">
            {/* Tabs */}
            <div className="flex gap-6 -mb-px overflow-x-auto">
              {/* Only show Transcript tab for audio reflections */}
              {!isJournalReflection && (
                <button
                  onClick={() => setActiveTab("transcript")}
                  className={`
                      flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                      ${activeTab === "transcript" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"}
                    `}
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Transcript</span>
                </button>
              )}
              <button
                onClick={() => setActiveTab("chat")}
                className={`
                    flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                    ${activeTab === "chat" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"}
                  `}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Chat</span>
              </button>
              <button
                onClick={() => setActiveTab("summary")}
                className={`
                    flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                    ${activeTab === "summary" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"}
                  `}
              >
                <BarChart2 className="w-4 h-4" />
                <span className="hidden sm:inline">Summary</span>
              </button>
            </div>

            {/* Mobile Settings & Insights Buttons (only show on transcript tab when sidebar is hidden) */}
            {activeTab === "transcript" && (
              <div className="flex lg:hidden items-center gap-2 mb-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMobileSpeakerSheet(true)}
                  className="gap-2"
                >
                  <Settings2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Settings</span>
                </Button>
                {speakersConfirmed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMobileInsights(true)}
                    className="gap-2"
                  >
                    <Lightbulb className="w-4 h-4" />
                    <span className="hidden sm:inline">Insights</span>
                  </Button>
                )}
              </div>
            )}

            {/* Search and Auto-scroll (only show on transcript tab) */}
            {activeTab === "transcript" && (
              <div className="hidden lg:flex items-center gap-2 mb-2">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-48 pl-8 pr-3 py-1.5 text-sm bg-muted/50 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:bg-background transition-colors"
                  />
                </div>

                {/* Auto-scroll toggle */}
                <label className="flex items-center gap-1.5 px-2.5 py-1.5 bg-muted/50 border border-input rounded-md cursor-pointer hover:bg-muted transition-colors">
                  <input
                    type="checkbox"
                    checked={autoScroll}
                    onChange={(e) => setAutoScroll(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-input text-primary focus:ring-2 focus:ring-ring"
                  />
                  <span className="text-xs font-medium whitespace-nowrap">
                    Auto-scroll
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - extra bottom padding for sticky audio player (not for journals), reduced on mobile chat view */}
      <main
        className={`flex-1 container mx-auto ${
          activeTab === "chat"
            ? "px-0 py-0 md:px-4 md:py-6" +
              (isJournalReflection ? "" : " md:pb-28")
            : "px-4 py-6" + (isJournalReflection ? "" : " pb-28")
        }`}
      >
        {/* Shared by banner */}
        {reflection.is_shared_copy && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 flex items-center gap-2">
            <Share2 className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Shared by{" "}
              <span className="font-medium">
                {reflection.shared_by_display_name || "another user"}
              </span>
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content Area */}
          <div
            className={`
                ${activeTab === "transcript" ? "lg:col-span-8" : "lg:col-span-12"}
                ${activeTab === "chat" ? "" : "space-y-6"}
              `}
          >
            {activeTab === "transcript" && (
              <>
                {/* Speaker Confirmation Banner - only show after data is loaded to prevent flash */}
                {!transcriptLoading &&
                  !transcriptFetching &&
                  !speakersConfirmed && (
                    <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <Users className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                            Confirm Speaker Identities
                          </h4>
                          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                            Please confirm who is speaking in this conversation
                            to unlock analysis, feedback, and chat features.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => setShowSpeakerWizard(true)}
                          className="flex-shrink-0"
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Confirm Speakers
                        </Button>
                      </div>
                    </div>
                  )}
                {/* Secondary binding banner for confirmed but unreviewed */}
                {/* Only show after transcript is fully loaded (not loading or refetching) to prevent flash */}
                {!transcriptLoading &&
                  !transcriptFetching &&
                  speakersConfirmed &&
                  !dismissedBindingBanner &&
                  transcript?.binding_status &&
                  transcript.binding_status !== "user_confirmed" && (
                    <SpeakerBindingBanner
                      bindingStatus={transcript.binding_status}
                      onReviewSpeakers={() => setShowSpeakerWizard(true)}
                      onDismiss={() => setDismissedBindingBanner(true)}
                      className="mb-4"
                    />
                  )}
                {transcript && (
                  <TranscriptStatsBar
                    hasKeyMoments={hasKeyMoments}
                    showKeyMoments={showKeyMoments}
                    onToggleKeyMoments={handleToggleKeyMoments}
                  />
                )}
                {transcriptLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
                    <p className="text-muted-foreground">
                      Loading transcript...
                    </p>
                  </div>
                ) : segments.length > 0 ? (
                  <Card className="border-none shadow-none bg-transparent sm:bg-card sm:border sm:shadow-sm">
                    <div className="p-2 sm:p-4">
                      <TranscriptView
                        ref={transcriptViewRef}
                        segments={segments}
                        speakers={speakers}
                        currentTimeMs={currentTimeMs}
                        onSeek={handleSeek}
                        transcriptId={transcriptId}
                        searchQuery={searchQuery}
                        autoScroll={autoScroll}
                        onAutoScrollChange={setAutoScroll}
                        speakersConfirmed={speakersConfirmed}
                        keyMomentMap={keyMomentMap}
                      />
                    </div>
                  </Card>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No transcript available for this recording.</p>
                  </div>
                )}
              </>
            )}

            {activeTab === "summary" && (
              <div className="max-w-3xl mx-auto py-8">
                {/* Block summary for audio reflections when speakers not confirmed */}
                {!isJournalReflection && !speakersConfirmed ? (
                  <SpeakerConfirmationRequired
                    feature="summary"
                    onConfirmSpeakers={() => setShowSpeakerWizard(true)}
                  />
                ) : isJournalReflection ? (
                  // Journal reflection summary view
                  <Card className="p-6">
                    {isGeneratingSummary ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                        <p className="text-muted-foreground">
                          Generating summary from your conversation...
                        </p>
                      </div>
                    ) : feedbackLoading ? (
                      <div className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-muted-foreground">
                          Loading summary...
                        </p>
                      </div>
                    ) : feedback ? (
                      <div className="prose prose-sm max-w-none">
                        <h3 className="text-lg font-semibold mb-4">
                          Conversation Summary
                        </h3>
                        <div className="prose prose-sm max-w-none text-muted-foreground [&>h3]:text-foreground [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mt-4 [&>h3]:mb-2 [&>ul]:space-y-1 [&>ul]:mt-2 [&>p]:leading-relaxed">
                          <ReactMarkdown>
                            {feedback.feedback_text}
                          </ReactMarkdown>
                        </div>
                        <div className="mt-6 pt-4 border-t">
                          <Button
                            variant="outline"
                            onClick={handleGenerateSummary}
                            disabled={isGeneratingSummary}
                          >
                            {isGeneratingSummary ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Regenerating...
                              </>
                            ) : (
                              "Regenerate Summary"
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <MessageSquare className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold mb-2">
                          No Summary Yet
                        </h3>
                        <p className="text-muted-foreground mb-6">
                          Have a conversation first, then generate a summary of
                          your discussion.
                        </p>
                        <div className="flex gap-3 justify-center">
                          <Button
                            variant="outline"
                            onClick={() => setActiveTab("chat")}
                          >
                            Go to Chat
                          </Button>
                          <Button
                            onClick={handleGenerateSummary}
                            disabled={isGeneratingSummary}
                          >
                            {isGeneratingSummary ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Generating...
                              </>
                            ) : (
                              "Generate Summary"
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                ) : (
                  // Audio reflection summary view
                  <StatsOverview
                    speakers={speakers}
                    summary={annotationsData?.summary || null}
                    feedback={feedback || null}
                    sentiment={conversationSentiment ?? null}
                    isLoading={feedbackLoading || annotationsLoading}
                    isRegenerating={isRegeneratingFeedback}
                    summaryStatus={summaryStatus}
                    analysisStatus={analysisStatus}
                    onRetry={retryAnalysis}
                  />
                )}
              </div>
            )}

            {activeTab === "chat" && (
              <div className="md:max-w-3xl md:mx-auto h-full py-8">
                {/* Block chat for audio reflections when speakers not confirmed */}
                {!isJournalReflection && !speakersConfirmed ? (
                  <SpeakerConfirmationRequired
                    feature="chat"
                    onConfirmSpeakers={() => setShowSpeakerWizard(true)}
                  />
                ) : (
                  <Card className="h-full border-none shadow-none md:border md:shadow-sm overflow-hidden">
                    <TherapistChat reflectionId={reflectionId!} />
                  </Card>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Only visible on desktop in transcript view */}
          {activeTab === "transcript" && (
            <div className="hidden lg:block lg:col-span-4 space-y-6">
              <div className="sticky top-24 space-y-6">
                {/* Conversation Settings Panel */}
                <ConversationSettingsPanel
                  therapyStyleId={
                    reflection.therapy_style_id ||
                    feedback?.therapy_style?.id ||
                    null
                  }
                  onTherapyStyleChange={handleTherapyStyleChange}
                  speakers={speakers}
                  onSpeakerNameUpdate={handleSpeakerNameUpdate}
                  onReviewSpeakers={
                    transcriptId && audioUrl
                      ? () => setShowSpeakerWizard(true)
                      : undefined
                  }
                  speakersConfirmed={
                    transcript?.binding_status === "user_confirmed"
                  }
                />

                {/* Memory used indicator */}
                {speakerMemory && memoryPartner && (
                  <Card className="p-4">
                    <Link
                      to={`/partners/${memoryPartner.id}`}
                      className="flex items-start gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Brain className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                      <span>
                        Memory used — therapist had prior observations of{" "}
                        <strong className="text-foreground">
                          {memoryPartner.name}
                        </strong>
                      </span>
                    </Link>
                  </Card>
                )}

                {/* Conversation Insights */}
                <Card className="p-4">
                  <h3 className="text-sm font-semibold mb-3">
                    Conversation Insights
                  </h3>
                  <InsightsSidebar
                    annotations={annotationsData?.annotations || []}
                    isLoading={annotationsLoading}
                    insightsStatus={insightsStatus}
                    onAnnotationClick={handleAnnotationClick}
                    onRetry={retryAnalysis}
                  />
                </Card>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Sticky Player at Bottom - Hidden for journal reflections and on mobile when in chat view */}
      {audioUrl && !isJournalReflection && (
        <div
          className={`sticky bottom-0 z-50 p-3 pointer-events-none ${activeTab === "chat" ? "hidden md:block" : ""}`}
        >
          <div className="container mx-auto max-w-4xl pointer-events-auto">
            <div className="bg-card rounded-full shadow-lg border border-border">
              <AudioPlayer
                ref={audioPlayerRef}
                src={audioUrl}
                onTimeUpdate={handleTimeUpdate}
                variant="sticky"
              />
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          reflection={reflection}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Speaker Binding Wizard */}
      {transcriptId && audioUrl && (
        <SpeakerBindingWizard
          transcriptId={transcriptId}
          recordingId={recordingId}
          reflectionId={reflectionId}
          recordingAudioSrc={audioUrl}
          isOpen={showSpeakerWizard}
          onClose={() => setShowSpeakerWizard(false)}
          onSuccess={() => {
            // Refetch both transcript (for updated confirmation status) and reflection
            refetchTranscript();
            refetchReflection();
            setShowSpeakerWizard(false);
            setDismissedBindingBanner(false); // Reset to show updated status
          }}
          speakers={speakers}
          triggerAnalysis={!speakersConfirmed}
          conversationPartnerId={reflection?.conversation_partner_id}
          isWithPrimaryPartner={reflection?.is_with_primary_partner ?? false}
        />
      )}

      {/* Mobile Speaker Sheet */}
      <MobileSpeakerSheet
        open={showMobileSpeakerSheet}
        onOpenChange={setShowMobileSpeakerSheet}
        speakers={speakers}
        speakersConfirmed={transcript?.binding_status === "user_confirmed"}
        onUpdateSpeakerName={handleSpeakerNameUpdate}
        onConfirmBinding={
          transcriptId && audioUrl
            ? () => {
                setShowMobileSpeakerSheet(false);
                setShowSpeakerWizard(true);
              }
            : undefined
        }
        isWithPrimaryPartner={reflection.is_with_primary_partner}
        conversationPartnerId={reflection.conversation_partner_id}
        therapyStyleId={
          reflection.therapy_style_id || feedback?.therapy_style?.id || null
        }
        onTherapyStyleChange={handleTherapyStyleChange}
      />

      {/* Mobile Insights Dialog */}
      <Dialog open={showMobileInsights} onOpenChange={setShowMobileInsights}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5" />
              Conversation Insights
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <InsightsSidebar
              annotations={annotationsData?.annotations || []}
              isLoading={annotationsLoading}
              insightsStatus={insightsStatus}
              onAnnotationClick={(annotation) => {
                setShowMobileInsights(false);
                handleAnnotationClick(annotation);
              }}
              onRetry={retryAnalysis}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
